import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { authService } from '@/src/entities/auth/api/service';
import type { AuthInfo } from '@/src/shared/model/types';
import {
  clearAuth,
  getAccessToken,
  setAuth,
} from '@/src/shared/model/auth-store';

type RetryConfig = InternalAxiosRequestConfig & {
  _isRetry?: boolean;
};

export function getAxiosError(e: unknown): string | undefined {
  if (!axios.isAxiosError(e)) return;

  const data = e.response?.data as
    | { message?: string | string[] }
    | undefined;
  const message = data?.message;

  if (Array.isArray(message)) {
    return message.join(', ');
  }

  if (typeof message === 'string' && message.length > 0) {
    return message;
  }

  return e.message;
}

export async function logout() {
  try {
    await authService.logout();
  } catch {
    // session cookie may already be cleared
  }

  clearAuth();
}

export const client = axios.create({
  baseURL: '',
  withCredentials: true,
});

export const clientWithAuth = axios.create({
  baseURL: '',
  withCredentials: true,
});

let refreshPromise: Promise<AuthInfo> | null = null;

export async function refreshSession() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const result = await authService.refresh();
      setAuth(result);
      return result;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

clientWithAuth.interceptors.request.use((config) => {
  const accessToken = getAccessToken();

  if (config.headers && accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

clientWithAuth.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig | undefined;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._isRetry
    ) {
      originalRequest._isRetry = true;

      try {
        await refreshSession();
        return clientWithAuth.request(originalRequest);
      } catch (refreshError) {
        // Only drop the session when refresh itself says the cookie is invalid.
        // Network blips must not log the user out.
        if (
          axios.isAxiosError(refreshError) &&
          refreshError.response?.status === 401
        ) {
          clearAuth();
        }
      }
    }

    throw error;
  },
);
